import {useState, useEffect, useMemo, useCallback} from 'react';
import PropTypes from 'prop-types';
import DentistAvailabilityApi from '../api/DentistAvailabilityApi';
import DoctorApi from '../api/DoctorApi';
import './DentistAvailabilityManager.css';
import {problemMessage} from '../api/client';

const DentistAvailabilityManager = ({doctorId}) => {
  const [availabilities, setAvailabilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterType, setFilterType] = useState(() => {
    // Honour ?type=open|reject URL param set by dashboard links
    const params = new URLSearchParams(window.location.search);
    const t = params.get('type');
    return t === 'open' || t === 'reject' ? t : 'all';
  });
  const [interval, setInterval] = useState(15);
  const [startAmPm, setStartAmPm] = useState('AM');
  const [endAmPm, setEndAmPm] = useState('PM');

  const [formData, setFormData] = useState({
    availability_type: 'open',
    start_time: '',
    end_time: '',
    recurring_pattern: '',
    notes: '',
  });

  const loadDoctorSettings = useCallback(async () => {
    try {
      const doctor = await DoctorApi.getDoctor(doctorId);
      if (doctor.consultation_interval) {
        setInterval(doctor.consultation_interval);
      }
    } catch (err) {
      console.error('Failed to load doctor settings', err);
    }
  }, [doctorId]);

  const minuteOptions = useMemo(() => {
    const minutes = [];
    let currentMinute = 0;
    while (currentMinute < 60) {
      minutes.push(String(currentMinute).padStart(2, '0'));
      currentMinute += interval;
    }
    return minutes;
  }, [interval]);

  const hourOptions = Array.from({length: 12}, (_, i) => String(i + 1).padStart(2, '0'));

  const handleTimeChange = (field, part, value) => {
    const currentIso = formData[field] || new Date().toISOString();
    let [datePart, timePart] = currentIso.split('T');

    // Default time if missing
    if (!timePart) timePart = '09:00:00';

    let [hours, minutes] = timePart.split(':');
    let currentHour = parseInt(hours, 10);

    if (part === 'hour') {
      const newHour12 = parseInt(value, 10);
      const isPm = field === 'start_time' ? startAmPm === 'PM' : endAmPm === 'PM';

      if (isPm && newHour12 < 12) {
        currentHour = newHour12 + 12;
      } else if (!isPm && newHour12 === 12) {
        currentHour = 0;
      } else {
        currentHour = newHour12;
      }
    } else if (part === 'minute') {
      minutes = value;
    } else if (part === 'ampm') {
      const isPm = value === 'PM';
      if (field === 'start_time') setStartAmPm(value);
      if (field === 'end_time') setEndAmPm(value);

      // Adjust current hour based on new AM/PM
      if (isPm && currentHour < 12) {
        currentHour += 12;
      } else if (!isPm && currentHour >= 12) {
        currentHour -= 12;
      }
    }

    const newTime = `${String(currentHour).padStart(2, '0')}:${minutes}`;
    const newIso = `${datePart}T${newTime}`;

    handleInputChange({
      target: {
        name: field,
        value: newIso,
      },
    });
  };

  // Helper to get 12-hour format hour
  const getHour12 = (isoString) => {
    if (!isoString) return '';
    const hour24 = parseInt(isoString.split('T')[1].split(':')[0], 10);
    if (hour24 === 0) return '12';
    if (hour24 > 12) return String(hour24 - 12).padStart(2, '0');
    return String(hour24).padStart(2, '0');
  };

  const getMinute = (isoString) => {
    if (!isoString) return '';
    return isoString.split('T')[1].split(':')[1];
  };

  // Filter availabilities client-side
  const filteredAvailabilities = availabilities.filter((a) => {
    if (filterType === 'all') return true;
    return a.availability_type === filterType;
  });

  const loadAvailabilities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Always fetch all to allow client-side filtering
      const data = await DentistAvailabilityApi.getAvailability(doctorId, {});
      setAvailabilities(data);
    } catch (err) {
      setError('Failed to load availability windows. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [doctorId]);

  useEffect(() => {
    if (doctorId) {
      loadAvailabilities();
      loadDoctorSettings();
    }
  }, [doctorId, loadAvailabilities, loadDoctorSettings]);

  const handleInputChange = (e) => {
    const {name, value} = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      availability_type: 'open',
      start_time: '',
      end_time: '',
      recurring_pattern: '',
      notes: '',
    });
    setStartAmPm('AM');
    setEndAmPm('PM');
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate dates
      const startTime = new Date(formData.start_time);
      const endTime = new Date(formData.end_time);

      if (endTime <= startTime) {
        setError('End time must be after start time');
        setLoading(false);
        return;
      }

      // Convert to UTC ISO strings for backend
      const submissionData = {
        ...formData,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        timezone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      if (editingId) {
        // Update existing
        await DentistAvailabilityApi.updateAvailability(doctorId, editingId, submissionData);
      } else {
        // Create new
        await DentistAvailabilityApi.createAvailability(doctorId, submissionData);
      }

      resetForm();
      loadAvailabilities();
    } catch (err) {
      const errorMsg = editingId ? 'Failed to update availability' : 'Failed to create availability';
      // Overlaps (409) and invalid RRULEs (422) carry a specific message.
      setError(problemMessage(err, errorMsg));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getLocalDatetimeString = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleEdit = (availability) => {
    let recurrenceValue = availability.recurring_pattern || '';
    if (typeof recurrenceValue === 'object') {
      try {
        recurrenceValue = JSON.stringify(recurrenceValue);
      } catch (e) {
        console.error('Failed to stringify recurrence pattern', e);
        recurrenceValue = '';
      }
    }

    const startStr = getLocalDatetimeString(availability.start_time);
    const endStr = getLocalDatetimeString(availability.end_time);

    setFormData({
      availability_type: availability.availability_type,
      start_time: startStr,
      end_time: endStr,
      recurring_pattern: recurrenceValue,
      notes: availability.notes || '',
    });

    // Set AM/PM based on times
    const startHour = parseInt(startStr.split('T')[1].split(':')[0], 10);
    const endHour = parseInt(endStr.split('T')[1].split(':')[0], 10);
    setStartAmPm(startHour < 12 ? 'AM' : 'PM');
    setEndAmPm(endHour < 12 ? 'AM' : 'PM');

    setEditingId(availability.id);
    setShowForm(true);
  };

  const handleDelete = async (availabilityId) => {
    const confirmMsg = 'Are you sure you want to delete this ' + 'availability window?';
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await DentistAvailabilityApi.deleteAvailability(doctorId, availabilityId);
      loadAvailabilities();
    } catch (err) {
      setError('Failed to delete availability');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  /* Removed unused getMinDateTime */

  return (
    <div className="dentist-availability-manager">
      <div className="manager-header">
        <h2>Manage Your Availability</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)} disabled={loading}>
          {showForm ? 'Cancel' : '+ Add Availability Window'}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button type="button" className="close" aria-label="Dismiss" onClick={() => setError(null)}>
            <span>&times;</span>
          </button>
        </div>
      )}

      {showForm && (
        <div className="availability-form-container">
          <h3>{editingId ? 'Edit' : 'Add'} Availability Window</h3>
          <form onSubmit={handleSubmit} className="availability-form">
            <div className="form-row">
              <div className="form-group col-md-6">
                <label htmlFor="availability_type">
                  Type <span className="text-danger">*</span>
                </label>
                <select
                  id="availability_type"
                  name="availability_type"
                  className="form-control"
                  value={formData.availability_type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="open">✅ Open (Available)</option>
                  <option value="reject">🚫 Blocked (Unavailable)</option>
                </select>
                <small className="form-text text-muted">
                  {formData.availability_type === 'open'
                    ? 'Patients can book appointments during this time'
                    : 'You will not accept appointments during this time'}
                </small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group col-md-6">
                <label>
                  Start Time <span className="text-danger">*</span>
                </label>
                <div className="d-flex align-items-center">
                  <input
                    type="date"
                    aria-label="Start date"
                    className="form-control mr-2"
                    style={{maxWidth: '160px'}}
                    value={formData.start_time ? formData.start_time.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.start_time ? formData.start_time.split('T')[1] : '09:00';
                      handleInputChange({
                        target: {
                          name: 'start_time',
                          value: `${date}T${time}`,
                        },
                      });
                    }}
                    required
                  />
                  <select
                    aria-label="Start hour"
                    className="form-control mr-1"
                    style={{maxWidth: '70px'}}
                    value={getHour12(formData.start_time)}
                    onChange={(e) => handleTimeChange('start_time', 'hour', e.target.value)}
                    required
                  >
                    <option value="">Hr</option>
                    {hourOptions.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                  <span className="mr-1">:</span>
                  <select
                    aria-label="Start minute"
                    className="form-control mr-2"
                    style={{maxWidth: '70px'}}
                    value={getMinute(formData.start_time)}
                    onChange={(e) => handleTimeChange('start_time', 'minute', e.target.value)}
                    required
                  >
                    <option value="">Min</option>
                    {minuteOptions.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>

                  <div className="btn-group btn-group-toggle">
                    <button
                      type="button"
                      aria-pressed={startAmPm === 'AM'}
                      className={`btn btn-sm ${startAmPm === 'AM' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleTimeChange('start_time', 'ampm', 'AM')}
                    >
                      AM
                    </button>
                    <button
                      type="button"
                      aria-pressed={startAmPm === 'PM'}
                      className={`btn btn-sm ${startAmPm === 'PM' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleTimeChange('start_time', 'ampm', 'PM')}
                    >
                      PM
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-group col-md-6">
                <label>
                  End Time <span className="text-danger">*</span>
                </label>
                <div className="d-flex align-items-center">
                  <input
                    type="date"
                    aria-label="End date"
                    className="form-control mr-2"
                    style={{maxWidth: '160px'}}
                    value={formData.end_time ? formData.end_time.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.end_time ? formData.end_time.split('T')[1] : '17:00';
                      handleInputChange({
                        target: {
                          name: 'end_time',
                          value: `${date}T${time}`,
                        },
                      });
                    }}
                    min={formData.start_time ? formData.start_time.split('T')[0] : ''}
                    required
                  />
                  <select
                    aria-label="End hour"
                    className="form-control mr-1"
                    style={{maxWidth: '70px'}}
                    value={getHour12(formData.end_time)}
                    onChange={(e) => handleTimeChange('end_time', 'hour', e.target.value)}
                    required
                  >
                    <option value="">Hr</option>
                    {hourOptions.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                  <span className="mr-1">:</span>
                  <select
                    aria-label="End minute"
                    className="form-control mr-2"
                    style={{maxWidth: '70px'}}
                    value={getMinute(formData.end_time)}
                    onChange={(e) => handleTimeChange('end_time', 'minute', e.target.value)}
                    required
                  >
                    <option value="">Min</option>
                    {minuteOptions.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>

                  <div className="btn-group btn-group-toggle">
                    <button
                      type="button"
                      aria-pressed={endAmPm === 'AM'}
                      className={`btn btn-sm ${endAmPm === 'AM' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleTimeChange('end_time', 'ampm', 'AM')}
                    >
                      AM
                    </button>
                    <button
                      type="button"
                      aria-pressed={endAmPm === 'PM'}
                      className={`btn btn-sm ${endAmPm === 'PM' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleTimeChange('end_time', 'ampm', 'PM')}
                    >
                      PM
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="recurring_pattern">Recurrence Rule (Optional)</label>
              <input
                type="text"
                id="recurring_pattern"
                name="recurring_pattern"
                className="form-control"
                value={formData.recurring_pattern}
                onChange={handleInputChange}
                placeholder="e.g., FREQ=WEEKLY;BYDAY=MO,WE,FR"
              />
              <small className="form-text text-muted">
                Use iCalendar RRULE format for recurring availability. Leave empty for one-time windows.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes (Optional)</label>
              <textarea
                id="notes"
                name="notes"
                className="form-control"
                rows="3"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Add any notes about this availability window..."
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-success" disabled={loading}>
                {loading ? 'Saving...' : editingId ? 'Update' : 'Create'}
                {' Window'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm} disabled={loading}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="availability-filters">
        <label>Filter by type:</label>
        <div className="btn-group" role="group">
          <button
            type="button"
            className={`btn ${filterType === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilterType('all')}
          >
            All
          </button>
          <button
            type="button"
            className={`btn ${filterType === 'open' ? 'btn-success' : 'btn-outline-success'}`}
            onClick={() => setFilterType('open')}
          >
            ✅ Open
          </button>
          <button
            type="button"
            className={`btn ${filterType === 'reject' ? 'btn-danger' : 'btn-outline-danger'}`}
            onClick={() => setFilterType('reject')}
          >
            🚫 Blocked
          </button>
        </div>
      </div>

      <div className="availability-list">
        {loading && <div className="loading-spinner">Loading...</div>}

        {!loading && filteredAvailabilities.length === 0 && (
          <div className="empty-state">
            <p>No availability windows found.</p>
            {filterType === 'all' && (
              <p>Click &quot;Add Availability Window&quot; to create your first one.</p>
            )}
          </div>
        )}

        {!loading && filteredAvailabilities.length > 0 && (
          <div className="availability-cards">
            {filteredAvailabilities.map((availability) => (
              <div key={availability.id} className={`availability-card ${availability.availability_type}`}>
                <div className="card-header">
                  <span className="availability-type-badge">
                    {availability.availability_type === 'open' ? '✅ Open' : '🚫 Blocked'}
                  </span>
                  <div className="card-actions">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleEdit(availability)}
                      title="Edit"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(availability.id)}
                      title="Delete"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>

                <div className="card-body">
                  <div className="time-info">
                    <div className="time-item">
                      <strong>Start:</strong> {formatDateTime(availability.start_time)}
                    </div>
                    <div className="time-item">
                      <strong>End:</strong> {formatDateTime(availability.end_time)}
                    </div>
                  </div>

                  {availability.recurring_pattern && (
                    <div className="recurrence-info">
                      <strong>Recurrence:</strong> {availability.recurring_pattern}
                    </div>
                  )}

                  {availability.notes && (
                    <div className="notes-info">
                      <strong>Notes:</strong> {availability.notes}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

DentistAvailabilityManager.propTypes = {
  doctorId: PropTypes.string.isRequired,
};

export default DentistAvailabilityManager;
